import React, { useMemo } from 'react'

import useApiService from 'services/api'
import { useGetLodgingDoor } from 'services/places'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyContentSections,
  CompanyPageLayout,
} from 'components/NewCompanyPage'
import { UnmatchedDoorsModal } from 'components/NewCompanyPage/Modals/UnmatchedDoorsModal'
import { InitialDoorData } from 'components/Samples/SampleRequestFormModal'

import { CompanyTypeSlug } from 'models/companies'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { LodgingAmenitiesSection } from 'components/DataExpansionComponents/GooglePlaces/Lodging/LodgingAmenitiesSection'
import { buildGooglePlaceSummaryContent } from './utils/googlePlacesHelpers'
import { buildLocationPlacesContent } from './utils/locationPlacesHelper'
import useDocumentTitle from 'components/useDocumentTitle'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

export function NewLodgingLocationPage({ lodgingID }: { lodgingID: number }) {
  const api = useApiService()
  const { data: doorProxyData, isLoading } = useGetLodgingDoor(lodgingID)
  const { preferences } = usePreferences()
  const { enableLodgingHotelAmenities } = useFeatureFlagService()

  const chainProxyId = doorProxyData?.chain_proxy.id
  const { data: companyData } = api.useGetCompany(chainProxyId ?? 0)

  // Set document title
  useDocumentTitle(
    `${doorProxyData?.lodging?.name || 'Not Found'} (Lodging Door) - First Bite`
  )

  // Modal state management
  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  // Pre-compute door data for sample requests to avoid redundant API calls
  // The data is already loaded via useGetLodgingDoor
  const initialDoorData: InitialDoorData | undefined = useMemo(() => {
    if (!doorProxyData?.lodging || !companyData) return undefined

    const lodging = doorProxyData.lodging
    return {
      name: lodging.name || '',
      address: lodging.address || '',
      city: lodging.city || '',
      state: lodging.state || '',
      zipcode: lodging.zipcode || '',
      companyName: companyData.name || '',
    }
  }, [doorProxyData?.lodging, companyData])

  const entityBreadcrumbs = useMemo(
    () => [
      ...(companyData?.name
        ? [
            {
              title: 'Company',
              label: companyData.name,
              path: `/lodging/chain/${preferences?.company_type_prefix_map[ContactCompanyType.Lodging]}${companyData.lodging_id}`,
            },
          ]
        : []),
      ...(doorProxyData?.lodging?.name
        ? [{ title: 'Door', label: doorProxyData.lodging.name }]
        : []),
    ],
    [
      companyData?.name,
      companyData?.lodging_id,
      preferences?.company_type_prefix_map,
      doorProxyData?.lodging?.name,
    ]
  )

  if (isLoading || !companyData) {
    return <LoadingScreen />
  }

  const summaryTabContent = [
    ...buildGooglePlaceSummaryContent({
      placeData: doorProxyData?.lodging,
      onViewDoorsClick: () => setShowDoorsModal(true),
      hideDoorsMetric: true,
    }),
    ...(enableLodgingHotelAmenities
      ? [
          <LodgingAmenitiesSection
            key="amenities"
            data={doorProxyData?.lodging}
          />,
        ]
      : []),
  ]

  const locationDetailsTabContent = buildLocationPlacesContent({
    addressInfo: {
      city: doorProxyData?.lodging?.city,
      state: doorProxyData?.lodging?.state,
      street: doorProxyData?.lodging?.address,
      zipcode: doorProxyData?.lodging?.zipcode,
      latitude: doorProxyData?.lodging?.latitude,
      longitude: doorProxyData?.lodging?.longitude,
    },
    phone: doorProxyData?.lodging?.phone,
    googlePlaceUrl: doorProxyData?.lodging?.place_url,
  })

  return (
    <>
      <CompanyPageLayout
        chainProxyId={companyData?.id as number}
        breadcrumbRoot={
          companyData?.sub_category?.name || 'Lodging & Hospitality'
        }
        breadcrumbs={[
          {
            title: 'Lodging',
            breadcrumb: companyData?.name || '',
            url: `/lodging/chain/${preferences?.company_type_prefix_map[ContactCompanyType.Lodging]}${companyData?.lodging_id}`,
          },
          {
            title: 'Door',
            breadcrumb: doorProxyData?.lodging?.name || '',
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        categoryInfo={{
          categorySlug: 'commercial',
          subCategorySlug: 'lodging-hospitality',
        }}
        sidebar={{
          header: {
            name: doorProxyData?.lodging?.name || '',
            domain: companyData?.domain || '',
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={companyData?.id as number}
          doorProxyId={doorProxyData?.id}
          companyName={doorProxyData?.lodging?.name || ''}
          websiteAddress={companyData?.domain || ''}
          companyTypeSlug={companyData?.sub_category?.slug as CompanyTypeSlug}
          tableTypeSlug="lodging-hospitality-doors"
          isUserCreated={false}
          isDoorPage={true}
          initialDoorData={initialDoorData}
        />
      </CompanyPageLayout>

      {/* Unmatched Doors Modal */}
      {companyData && (
        <UnmatchedDoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={companyData?.id as number}
          chainData={{
            id: companyData?.id as number,
            name: companyData?.name || '',
            domain: companyData?.domain || '',
          }}
          subCategory={companyData?.sub_category}
          companyName={companyData?.name || ''}
        />
      )}
    </>
  )
}
